"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SourceFrontendUpdateTracker = void 0;
const tiny_emitter_1 = require("tiny-emitter");
const axios_1 = __importDefault(require("axios"));
const frontend_update_tracker_1 = require("./frontend-update-tracker");
const RETRY_DELAY_BASE_MS = 500;
class SourceFrontendUpdateTracker {
    constructor({ sourceUrl, intervalMs = 30000 }) {
        this.baseline = null;
        this.timer = null;
        this.emitter = new tiny_emitter_1.TinyEmitter();
        this._initialized = false;
        if (!sourceUrl) {
            throw new Error('Source URL of frontend fragments is required');
        }
        this.sourceUrl = sourceUrl;
        this.intervalMs = intervalMs;
    }
    get initialized() {
        return this._initialized;
    }
    get started() {
        return this.timer !== null;
    }
    onUpdate(handler) {
        this.emitter.on('update', handler);
    }
    onError(handler) {
        this.emitter.on('error', handler);
    }
    async start() {
        if (this._initialized) {
            throw new Error(`${frontend_update_tracker_1.FrontendUpdateTracker.name} can only be initialized once`);
        }
        this._initialized = true;
        try {
            await this.setupBaselineVersions();
            await this.setTimer();
        }
        catch (error) {
            this.emitError(error);
        }
    }
    async setTimer() {
        this.timer = setInterval(() => this.getAndCompareFragmentVersions(), this.intervalMs);
    }
    emitUpdate(differenceVersions) {
        this.emitter.emit('update', differenceVersions);
    }
    emitError(error) {
        this.emitter.emit('error', error);
    }
    async setupBaselineVersions() {
        let attempt = 0;
        while (true) {
            try {
                const fragments = await this.getFragmentVersions();
                this.baseline = this.getBaselineMap(fragments);
                return;
            }
            catch (error) {
                attempt++;
                this.emitError(error);
                await new Promise(res => setTimeout(res, RETRY_DELAY_BASE_MS * attempt));
            }
        }
    }
    getBaselineMap(fragments) {
        return new Map(fragments.map(fragment => [fragment.name, { ...fragment, isUpdated: false }]));
    }
    async getAndCompareFragmentVersions() {
        try {
            const fragments = await this.getFragmentVersions();
            if (!this.baseline) {
                this.baseline = this.getBaselineMap(fragments);
                return;
            }
            const differenceVersions = this.hasDifference(fragments);
            if (differenceVersions.length) {
                this.emitUpdate(differenceVersions);
            }
        }
        catch (error) {
            this.emitError(error);
        }
    }
    async getFragmentVersions() {
        const response = await axios_1.default.get(this.sourceUrl, { headers: { 'Cache-Control': 'no-store' } });
        return response.data;
    }
    hasDifference(current) {
        var _a;
        const updatedFragments = [];
        if (this.baseline === null) {
            this.baseline = new Map(current.map(fragment => [fragment.name, { ...fragment, isUpdated: true }]));
            return current.map(fragment => fragment.name);
        }
        for (const fragment of current) {
            const baselineFragment = this.baseline.get(fragment.name);
            if (!baselineFragment) {
                updatedFragments.push(fragment.name);
                this.baseline.set(fragment.name, { ...fragment, isUpdated: true });
                continue;
            }
            if (baselineFragment.isUpdated) {
                continue;
            }
            if (baselineFragment.version !== fragment.version) {
                updatedFragments.push(fragment.name);
                this.baseline.get(fragment.name).isUpdated = true;
            }
        }
        for (const fragment of ((_a = this.baseline) === null || _a === void 0 ? void 0 : _a.values()) || []) {
            if (!current.some(f => f.name === fragment.name)) {
                updatedFragments.push(fragment.name);
                this.baseline.get(fragment.name).isUpdated = true;
            }
        }
        return updatedFragments;
    }
}
exports.SourceFrontendUpdateTracker = SourceFrontendUpdateTracker;
